import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { environment } from '../../environments/environment';

export interface GapPurchaseItem {
  item_id: string;
  item_name: string;
  item_variant?: string;
  price: number;
  quantity: number;
}

export interface GapPurchasePayload {
  transaction_id: string;
  value: number;
  currency?: string;
  items: GapPurchaseItem[];
}

export interface GapFunnelStepPayload {
  step: number;
  path: string;
  flow?: 'policy' | 'warranty';
}

export interface GapGenerateLeadPayload {
  policy_no: string;
  flow: 'policy' | 'warranty';
  product_code?: string;
}

export type GapDataLayerEntry = Record<string, unknown> | unknown[];

declare global {
  interface Window {
    dataLayer?: GapDataLayerEntry[];
    gtag?: (...args: unknown[]) => void;
    __GAP_GTM_LOADED?: boolean;
  }
}

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  constructor(@Inject(PLATFORM_ID) private readonly platformId: object) {}

  isEnabled(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  pageView(pagePath: string, pageTitle?: string): void {
    this.push({
      event: 'virtual_page_view',
      page_path: pagePath,
      page_title: pageTitle ?? '',
    });
  }

  funnelStep(payload: GapFunnelStepPayload): void {
    this.push({
      event: 'gap_funnel_step',
      funnel_step: payload.step,
      page_path: payload.path,
      funnel_flow: payload.flow ?? 'policy',
    });
  }

  beginCheckout(flow: 'policy' | 'warranty', path: string): void {
    this.push({
      event: 'begin_checkout',
      funnel_flow: flow,
      page_path: path,
    });
  }

  generateLead(payload: GapGenerateLeadPayload): void {
    this.push({
      event: 'generate_lead',
      policy_no: payload.policy_no,
      funnel_flow: payload.flow,
      product_code: payload.product_code ?? '',
    });
  }

  purchase(payload: GapPurchasePayload): void {
    if (!this.isEnabled()) {
      return;
    }

    const key = `gap_purchase_${payload.transaction_id}`;
    if (sessionStorage.getItem(key)) {
      return;
    }
    sessionStorage.setItem(key, '1');

    this.push({ ecommerce: null });
    this.push({
      event: 'purchase',
      ecommerce: {
        transaction_id: payload.transaction_id,
        value: payload.value,
        currency: payload.currency ?? 'PLN',
        items: payload.items,
      },
    });
  }

  groszeToPln(grosze: number | null | undefined): number {
    if (grosze == null || Number.isNaN(grosze)) {
      return 0;
    }
    return grosze / 100;
  }

  private push(data: Record<string, unknown>): void {
    if (!this.isEnabled()) {
      return;
    }

    if (environment.analyticsDebug) {
      const label = data['event'] ?? '(dataLayer)';
      console.log('[GAP analytics]', label, data);
    }

    // Local dataLayer only. Hits reach GA when GTM is loaded (resolveGtmId in production).
    window.dataLayer = window.dataLayer ?? [];
    window.dataLayer.push(data);
  }
}
